import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { StorageService } from '../services/storageService';
import { GeminiService } from '../services/geminiService';
import { User, UserRole, IssueStatus, IssuePriority } from '../types';
import { CATEGORIES, CATEGORY_STRUCTURE, BRANCH_LOCATIONS, ISSUE_PLACES } from '../constants';
import { Wand2, Loader2, Upload, X, AlertCircle, CheckCircle2, Circle, User as UserIcon } from 'lucide-react';
import { useAuth } from '../components/AuthContext';

const IssueForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;
  const { user, loading: authLoading } = useAuth();
  
  // Form State
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [priority, setPriority] = useState<string>('');
  const [location, setLocation] = useState('');
  const [place, setPlace] = useState('');
  const [reportingPerson, setReportingPerson] = useState('');
  
  // Validation State
  const [showErrors, setShowErrors] = useState(false);
  
  // UI State
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingIssue, setLoadingIssue] = useState(false);

  // Store File objects for upload, plus a local preview URL
  const [pendingFiles, setPendingFiles] = useState<{ file: File; previewUrl: string }[]>([]);

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const MAX_ATTACHMENTS = 5;

  // Clean up object URLs on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      pendingFiles.forEach(p => URL.revokeObjectURL(p.previewUrl));
    };
  }, [pendingFiles]);

  useEffect(() => {
    if (!authLoading && !user) {
        navigate('/login');
        return;
    }
    
    if (user) {
        if (user.role === UserRole.TECHNICIAN && !isEditing) {
            navigate('/dashboard');
            return;
        }

        // Auto-restrict location for Staff and Manager with assigned outlets
        if ((user.role === UserRole.STAFF || user.role === UserRole.MANAGER) && user.assignedOutlets?.length) {
          const outlets = user.assignedOutlets;
          if (outlets.length === 1 && outlets[0] !== '__ALL__') {
            setLocation(outlets[0]);
          }
        }

        if (id) {
          const initEditing = async () => {
              setLoadingIssue(true);
              try {
                const issue = await StorageService.getIssueById(id);
                if (issue) {
                  if (user.role !== UserRole.ADMIN && issue.reportedBy !== user.id) {
                    alert("You do not have permission to edit this issue.");
                    navigate('/dashboard');
                    return;
                  }

                  setTitle(issue.title);
                  setDescription(issue.description);
                  setCategory(issue.category);
                  setSubCategory(issue.subCategory || '');
                  setPriority(issue.priority);
                  setReportingPerson(issue.reportedByName);
                  if (issue.location) setLocation(issue.location);
                  if (issue.place) setPlace(issue.place);
                } else {
                  navigate('/issues');
                }
              } catch (error) {
                console.error("Failed to load issue", error);
                navigate('/issues');
              } finally {
                setLoadingIssue(false);
              }
          };
          initEditing();
        }
    }
  }, [navigate, id, isEditing, user, authLoading]);

  const handleCategoryChange = (val: string) => {
    setCategory(val);
    setSubCategory(''); 
  };

  const handleAIAutoFill = async () => {
    if (!title) return;
    setIsGenerating(true);
    const generatedDesc = await GeminiService.expandIssueDescription(title, category || 'General');
    setDescription(generatedDesc);
    setIsGenerating(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = MAX_ATTACHMENTS - pendingFiles.length;
    if (remaining <= 0) {
      alert(`You can upload a maximum of ${MAX_ATTACHMENTS} images.`);
      e.target.value = '';
      return;
    }

    const toProcess = files.slice(0, remaining);
    if (files.length > remaining) {
      alert(`Only ${remaining} more image(s) can be added. The rest were ignored.`);
    }

    const newEntries: { file: File; previewUrl: string }[] = [];
    toProcess.forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        alert(`"${file.name}" is too large. Please use images under 5MB.`);
        return;
      }
      newEntries.push({ file, previewUrl: URL.createObjectURL(file) });
    });

    setPendingFiles(prev => [...prev, ...newEntries]);
    e.target.value = '';
  };

  const handleRemoveAttachment = (index: number) => {
    setPendingFiles(prev => {
      URL.revokeObjectURL(prev[index].previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const isFormValid = title.trim() !== '' && 
                      description.trim() !== '' && 
                      category !== '' && 
                      subCategory !== '' &&
                      location !== '' && 
                      place !== '' && 
                      priority !== '' &&
                      reportingPerson.trim() !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowErrors(true);

    if (!isFormValid || !user) {
        return;
    }

    setIsSubmitting(true);

    try {
        // Upload files to Supabase Storage and get back URL-based attachment objects
        const uploadedAttachments = await Promise.all(
          pendingFiles.map(p => StorageService.uploadAttachment(p.file))
        );

        const issuePayload: any = {
            title,
            description,
            category,
            subCategory,
            priority: priority as IssuePriority,
            location: location,
            place: place,
            reportedByName: reportingPerson.trim(), // Note: backend mapper might expect reportedByName
        };

        if (isEditing) {
          issuePayload.id = id;
          if (uploadedAttachments.length > 0) {
            issuePayload.attachments = uploadedAttachments;
          }
        } else {
          issuePayload.status = IssueStatus.OPEN;
          issuePayload.reportedBy = user.id;
          issuePayload.reportedByName = user.name;
          issuePayload.comments = [];
          issuePayload.attachments = uploadedAttachments;
        }

        await StorageService.saveIssue(issuePayload);
        
        if (isEditing) {
          navigate(`/issues/${id}`);
        } else {
          navigate('/issues');
        }
    } catch (error) {
        console.error("Failed to submit issue", error);
        alert("Failed to submit issue. Please try again.");
    } finally {
        setIsSubmitting(false);
    }
  };

  if (authLoading || loadingIssue) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const LabelWithError = ({ label, isError }: { label: string, isError: boolean }) => (
    <div className="flex justify-between items-center mb-1.5">
      <label className={`block text-[13px] font-semibold ${isError ? 'text-red-600' : 'text-slate-600'}`}>{label}</label>
      {isError && (
        <span className="text-[10px] font-black text-red-600 uppercase flex items-center bg-red-50 px-1.5 py-0.5 rounded border border-red-100">
          <AlertCircle className="w-2.5 h-2.5 mr-1" />
          Required
        </span>
      )}
    </div>
  );

  const subCategories = category ? CATEGORY_STRUCTURE[category] : [];

  const assignedOutlets = user?.assignedOutlets || [];
  const isLocationLocked =
    (user?.role === UserRole.STAFF || user?.role === UserRole.MANAGER) &&
    assignedOutlets.length > 0 &&
    !assignedOutlets.includes('__ALL__');
  const availableLocations = isLocationLocked ? assignedOutlets : BRANCH_LOCATIONS;

  if (!user) return null;

  return (
    <Layout user={user}>
      <div className="max-w-4xl mx-auto pb-8 sm:pb-12">
        <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                {isEditing ? 'Edit Issue' : 'Report New Issue'}
            </h1>
            <p className="text-slate-500 mt-1">Please provide accurate details to help our technical team assist you faster.</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8 transition-all hover:shadow-md">
            
            {/* 1. Title */}
            <div className="space-y-2">
                <LabelWithError label="Issue Title" isError={showErrors && !title.trim()} />
                <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Brief summary of the problem"
                    className={`w-full px-4 py-3 sm:py-3 border rounded-xl focus:ring-4 outline-none bg-white text-slate-900 transition-all font-medium min-h-[44px]
                    ${showErrors && !title.trim() ? 'border-red-300 ring-red-50 focus:ring-red-100' : 'border-slate-200 ring-blue-50 focus:ring-blue-100 focus:border-blue-500'}`}
                />
            </div>

            {/* 2. Main Category Selection */}
            <div>
                <LabelWithError label="Main Category" isError={showErrors && !category} />
                <select
                    value={category}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-4 outline-none bg-white text-slate-900 transition-all cursor-pointer min-h-[44px]
                    ${showErrors && !category ? 'border-red-300 ring-red-50' : 'border-slate-200 ring-blue-50 focus:border-blue-500'}`}
                >
                    <option value="">-- Select Category --</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>

            {/* 3. Sub-Category Grid */}
            {category && (
              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 animate-fade-in shadow-inner">
                 <LabelWithError label={`Select specific ${category} issue (Sub-category)`} isError={showErrors && !subCategory} />
                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mt-4">
                    {subCategories.map(sub => (
                      <button
                        key={sub}
                        type="button"
                        onClick={() => setSubCategory(sub)}
                        className={`group flex items-center text-left px-3 sm:px-4 py-3 sm:py-4 rounded-xl border transition-all duration-200 min-h-[44px]
                          ${subCategory === sub 
                            ? 'bg-blue-600 border-blue-600 text-white shadow-lg scale-[1.02]' 
                            : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300 hover:bg-blue-50'
                          }`}
                      >
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mr-3 flex-shrink-0 transition-colors
                          ${subCategory === sub 
                            ? 'bg-white border-white text-blue-600' 
                            : 'bg-slate-50 border-slate-200 group-hover:border-blue-200'}
                        `}>
                          {subCategory === sub ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-3 h-3 text-slate-300" />}
                        </div>
                        <span className="text-sm font-bold leading-tight">{sub}</span>
                      </button>
                    ))}
                 </div>
              </div>
            )}

            {/* 4. Location & Place Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8">
                <div>
                    <LabelWithError label="Location (Branch)" isError={showErrors && !location} />
                    {isLocationLocked && availableLocations.length === 1 ? (
                      <div className={`w-full px-4 py-3 border rounded-xl bg-slate-50 text-slate-700 font-medium min-h-[44px] flex items-center
                        ${showErrors && !location ? 'border-red-300' : 'border-slate-200'}`}>
                        {availableLocations[0]}
                      </div>
                    ) : (
                      <select
                          value={location}
                          onChange={(e) => setLocation(e.target.value)}
                          className={`w-full px-4 py-3 border rounded-xl focus:ring-4 outline-none bg-white text-slate-900 transition-all cursor-pointer min-h-[44px]
                          ${showErrors && !location ? 'border-red-300 ring-red-50' : 'border-slate-200 ring-blue-50 focus:border-blue-500'}`}
                      >
                          <option value="">-- Select Location --</option>
                          {availableLocations.map(loc => (
                            <option key={loc} value={loc}>{loc}</option>
                          ))}
                      </select>
                    )}
                    {isLocationLocked && (
                      <p className="text-xs text-slate-400 mt-1">Restricted to your assigned outlet</p>
                    )}
                </div>
                <div>
                    <LabelWithError label="Place" isError={showErrors && !place} />
                    <select
                        value={place}
                        onChange={(e) => setPlace(e.target.value)}
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-4 outline-none bg-white text-slate-900 transition-all cursor-pointer min-h-[44px]
                        ${showErrors && !place ? 'border-red-300 ring-red-50' : 'border-slate-200 ring-blue-50 focus:border-blue-500'}`}
                    >
                        <option value="">-- Select Place --</option>
                        {ISSUE_PLACES.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>
            </div>

            {/* 4b. Reporting Person */}
            <div>
                <LabelWithError label="Reporting Person" isError={showErrors && !reportingPerson.trim()} />
                <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                    <input
                        type="text"
                        value={reportingPerson}
                        onChange={(e) => setReportingPerson(e.target.value)}
                        placeholder="Enter the name of the person reporting this issue"
                        className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-4 outline-none bg-white text-slate-900 transition-all min-h-[44px]
                        ${showErrors && !reportingPerson.trim() ? 'border-red-300 ring-red-50' : 'border-slate-200 ring-blue-50 focus:border-blue-500'}`}
                    />
                </div>
            </div>

            {/* 5. Priority Selection */}
            <div>
                <LabelWithError label="Priority Level" isError={showErrors && !priority} />
                <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-4 outline-none bg-white text-slate-900 transition-all cursor-pointer font-bold min-h-[44px]
                    ${showErrors && !priority ? 'border-red-300 ring-red-50' : 'border-slate-200 ring-blue-50 focus:border-blue-500'}`}
                >
                    <option value="">-- Select Priority --</option>
                    {Object.values(IssuePriority).map(p => (
                        <option key={p} value={p} className={p === 'CRITICAL' ? 'text-red-600 font-bold' : ''}>
                            {p}
                        </option>
                    ))}
                </select>
            </div>

            {/* 6. Description */}
            <div className="relative group">
                <LabelWithError label="Detailed Description" isError={showErrors && !description.trim()} />
                <textarea
                    rows={6}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Provide more context here. What happened? Any error codes? When did it start?"
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-4 outline-none resize-none bg-white text-slate-900 transition-all min-h-[130px]
                        ${showErrors && !description.trim() ? 'border-red-300 ring-red-50' : 'border-slate-200 ring-blue-50 focus:border-blue-500'}`}
                />
            </div>

            {/* 7. Attachment */}
            <div>
                <label className="block text-[13px] font-semibold text-slate-600 mb-2">
                    {isEditing ? 'New Attachments (Overrides existing)' : 'Add Photos/Screenshots'} <span className="font-normal text-slate-400">(up to 5 images)</span>
                </label>

                {/* Uploaded previews */}
                {pendingFiles.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {pendingFiles.map((att, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-lg overflow-hidden mr-3 border border-emerald-200">
                            <img src={att.previewUrl} className="w-full h-full object-cover" alt="preview" />
                          </div>
                          <span className="text-sm text-emerald-800 font-bold truncate max-w-xs">{att.file.name}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(index)}
                          className="text-emerald-600 hover:text-red-500 p-2 transition-colors bg-white rounded-lg"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Empty upload box */}
                {pendingFiles.length < MAX_ATTACHMENTS && (
                    <div className="border-2 border-dashed border-slate-200 rounded-2xl p-6 sm:p-8 flex flex-col items-center justify-center text-slate-500 hover:bg-blue-50 hover:border-blue-300 transition-all cursor-pointer relative bg-slate-50/50 min-h-[140px] sm:min-h-[160px]">
                        <input 
                            type="file" 
                            accept="image/*" 
                            multiple
                            onChange={handleFileChange}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <div className="p-3 bg-white rounded-full shadow-sm mb-3">
                            <Upload className="w-6 h-6 text-blue-600" />
                        </div>
                        <span className="text-sm font-medium">Click to upload ({pendingFiles.length}/{MAX_ATTACHMENTS} added · Max 5MB each)</span>
                        <span className="text-xs text-slate-400 mt-1">Supports JPG, PNG</span>
                    </div>
                )}
            </div>

          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4 sm:pt-6">
            <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full sm:w-auto flex items-center justify-center px-10 py-4 rounded-xl text-white font-bold text-lg transition-all shadow-xl
                ${isSubmitting ? 'bg-slate-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 hover:scale-[1.02] active:scale-[0.98] shadow-blue-200'}`}
            >
                {isSubmitting ? (
                    <><Loader2 className="w-6 h-6 animate-spin mr-2" /> Submitting...</>
                ) : (
                    <>{isEditing ? 'Save Changes' : 'Submit Issue Report'}</>
                )}
            </button>
          </div>
          
        </form>
      </div>
    </Layout>
  );
};

export default IssueForm;
